import React, { useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Link,
  Paper, Snackbar, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { BatchApplyResponse, outcomesApi } from '../api/services';
import { extractApiError } from '../utils/apiErrors';
import { CombinedDecisionTable } from '../components/outcomes/CombinedDecisionTable';
import { LocationInventoryChart } from '../components/outcomes/LocationInventoryChart';
import { DigestOutcomes } from '../components/outcomes/DigestOutcomes';
import { EdgeReviewBatch } from '../components/outcomes/EdgeReviewBatch';
import { every, longDay, moneyCents } from '../components/outcomes/batch';

// The per-location Edge Review, written for Marah prepping a branch SYNC
// (sc-6441): the purpose and three steps, the cadence + one table of every item
// with batch Apply, the definitions on the page, then — unchanged — the
// inventory chart, the order history, and digest outcomes. Applying writes
// through the Outcomes batch endpoint in one transaction (BatchApply).

const h2 = { fontSize: 20, fontWeight: 500 } as const;

// "Reviewed Oct 7, 2026 · Restocked every week on the stock truck · Dead stock $2,262.60"
export const factsLine = (review: {
  reviewed: boolean; run_on: string | null; restock_interval_weeks: number; uses_stock_truck: boolean;
  dead_stock: { value_cents: number } | null;
}): string => {
  const restock = `Restocked ${every(review.restock_interval_weeks)}${review.uses_stock_truck ? ' on the stock truck' : ''}`;
  if (!review.reviewed || !review.run_on) return `No review yet · ${restock}`;
  return [`Reviewed ${longDay(review.run_on)}`, restock, `Dead stock ${moneyCents(review.dead_stock?.value_cents ?? 0)}`].join(' · ');
};

const EdgeReviewLocationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data: review, isLoading, error } = useQuery({
    queryKey: ['outcomes-review', id],
    queryFn: () => outcomesApi.review(id!),
    enabled: !!id,
    staleTime: 30000,
  });

  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [regenConfirm, setRegenConfirm] = useState(false);

  // A max or the cadence moved: the review-backed charts must follow.
  const invalidateDependents = () => {
    queryClient.invalidateQueries({ queryKey: ['outcomes-locations'] });
    queryClient.invalidateQueries({ queryKey: ['outcomes', 'inventory-chart'] });
    queryClient.invalidateQueries({ queryKey: ['outcomes', 'group-chart'] });
  };

  // On-demand regeneration: the endpoint returns the fresh review, so seed
  // the query cache directly (no refetch).
  const regenerate = useMutation({
    mutationFn: () => outcomesApi.regenerate(id!),
    onSuccess: (fresh) => {
      queryClient.setQueryData(['outcomes-review', id], fresh);
      invalidateDependents();
      setRegenConfirm(false);
      setSnackbar({ severity: 'success', message: 'Report regenerated.' });
    },
    onError: (e) => {
      setRegenConfirm(false);
      setSnackbar({ severity: 'error', message: extractApiError(e, 'Regenerate failed') });
    },
  });

  // Apply's dialog stays open on the email to copy; the page just takes the
  // fresh review (now read-only) and refreshes what depends on the maxes.
  const onApplied = (fresh: BatchApplyResponse) => {
    queryClient.setQueryData(['outcomes-review', id], fresh);
    invalidateDependents();
  };

  // Generate the customer-facing PDF: fetch the bytes (token-authenticated blob,
  // sc-5916), then trigger an object-URL download with the server filename.
  const generatePdf = useMutation({
    mutationFn: () => outcomesApi.reportPdf(id!),
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Defer revoke: revoking synchronously right after click can cancel the
      // in-flight download on some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 0);
      setSnackbar({ severity: 'success', message: 'PDF downloaded.' });
    },
    // The request is responseType:'blob', so on error the server's JSON {error}
    // arrives as a Blob — read it back before falling through to the generic message.
    onError: async (e) => {
      let message = 'PDF generation failed';
      const data = (e as { response?: { data?: unknown } })?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          message = parsed.error ?? parsed.errors?.[0]?.detail ?? message;
        } catch { /* keep default */ }
      } else {
        message = extractApiError(e, message);
      }
      setSnackbar({ severity: 'error', message });
    },
  });

  if (isLoading) {
    return <Box sx={{ p: 4, display: 'flex', gap: 2 }}><CircularProgress size={22} /><Typography>Loading review…</Typography></Box>;
  }
  if (error || !review) {
    return <Box sx={{ p: 4 }}><Alert severity="error">{extractApiError(error, 'Could not load the review')}</Alert></Box>;
  }

  return (
    // minmax(0, 1fr): a wide section (the order-history table) scrolls inside
    // its own box instead of widening the page on narrow screens.
    <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 3.5, minWidth: 0 }}>
      <Box component="header">
        <Link component={RouterLink} to="/outcomes" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <ArrowBackIcon fontSize="small" /> Edge Reviews
        </Link>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px 16px', flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h1" sx={{ fontSize: 22, fontWeight: 500, mb: 0.5 }}>{review.name}</Typography>
            <Typography color="text.secondary">{factsLine(review)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Regenerate is available for every monitored location, including those
                awaiting their first review — this is the path to the first report. */}
            <Button variant="outlined" onClick={() => setRegenConfirm(true)} disabled={regenerate.isPending}>
              {regenerate.isPending ? 'Regenerating…' : 'Regenerate'}
            </Button>
            {/* PDF needs a review to render; disabled until the first run. It stays
                after Apply and prints the same proposal (UI AC 10). */}
            <Button variant="outlined" onClick={() => generatePdf.mutate()} disabled={generatePdf.isPending || !review.reviewed}>
              {generatePdf.isPending ? 'Generating…' : 'PDF'}
            </Button>
          </Box>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ px: 2.25, py: 1.75, display: 'grid', gap: 1 }}>
        {/* The sequence up front (UI AC 2): the PDF before Apply, the email after. */}
        <Typography sx={{ fontWeight: 700 }}>Bring the PDF to the SYNC. Send the email after you apply.</Typography>
        <Box component="ol" sx={{ m: 0, pl: 3, display: 'grid', gap: 0.5 }}>
          <li>Before the SYNC, review what would change and download the PDF.</li>
          <li>At the SYNC, walk the customer through the PDF. Uncheck anything they want kept, with a note saying why, and type in any numbers of their own.</li>
          <li>After the SYNC, apply, then copy the email and send it to the branch.</li>
        </Box>
      </Paper>

      {review.batch && review.run_on ? (
        <EdgeReviewBatch locationId={review.location_id} locationName={review.name} batch={review.batch}
          refDay={review.run_on} onApplied={onApplied} />
      ) : (
        <Typography color="text.secondary">No review yet — use Regenerate to compute the first one.</Typography>
      )}

      {review.definitions.length > 0 && (
        <Box component="section" sx={{ display: 'grid', gap: 1.25 }}>
          <Typography component="h2" sx={h2}>How the numbers work</Typography>
          <Typography color="text.secondary">What each number on this page means. The same definitions the engine uses.</Typography>
          <Paper variant="outlined" component="dl" sx={{ m: 0, px: 2.25, py: 1.75, display: 'grid', gap: '10px 20px',
            gridTemplateColumns: { xs: '1fr', sm: 'minmax(130px, 180px) 1fr' } }}>
            {review.definitions.map((d) => (
              <React.Fragment key={d.term}>
                <Box component="dt" sx={{ fontWeight: 500 }}>{d.term}</Box>
                <Box component="dd" sx={{ m: 0, maxWidth: '78ch' }}>{d.text}</Box>
              </React.Fragment>
            ))}
          </Paper>
        </Box>
      )}

      {/* Below, unchanged (sc-6441 UI AC 8). */}
      <Box component="section">
        <Typography variant="h6" sx={{ mb: 1 }}>Inventory & HVA history — since go-live</Typography>
        <LocationInventoryChart locationId={review.location_id} />
      </Box>

      <Box component="section">
        <Typography variant="h6" sx={{ mb: 1 }}>Order history</Typography>
        <CombinedDecisionTable review={review} />
      </Box>

      <Box component="section">
        <Typography variant="h6" sx={{ mb: 1 }}>Digest outcomes — restock acceptance</Typography>
        <DigestOutcomes locationId={review.location_id} />
      </Box>

      {/* Regenerate confirm dialog — framed as a heavy operation */}
      <Dialog open={regenConfirm} onClose={() => !regenerate.isPending && setRegenConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Regenerate report — {review.name}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This recomputes the entire review for {review.name} from the latest inventory and order
            data, and reconciles its open recommendations. It&apos;s a heavy operation and may take a few
            seconds. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenConfirm(false)} disabled={regenerate.isPending}>Cancel</Button>
          <Button onClick={() => regenerate.mutate()} variant="contained" disabled={regenerate.isPending}>
            {regenerate.isPending ? 'Regenerating…' : 'Regenerate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar} autoHideDuration={6000} onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar?.severity || 'success'} onClose={() => setSnackbar(null)} sx={{ width: '100%' }}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EdgeReviewLocationPage;
